export type RecurrenceType = 'daily' | 'weekly' | 'monthly' | 'bimonthly' | 'quarterly' | 'semiannual' | 'annual' | 'lifetime' | 'custom';

export interface StatusCalculationData {
  lastTransactionDate: string | null;
  periodEndDate: string | null;
  recurrenceType: RecurrenceType | null;
}

export type CustomerStatus = 
  | { variant: 'default'; label: 'Ativo' }
  | { variant: 'secondary'; label: 'Em Risco' }
  | { variant: 'destructive'; label: 'Churned' }
  | { variant: 'outline'; label: 'Sem Transações' };

// Configuração de tolerâncias por tipo de plano (em dias)
const TOLERANCE_CONFIG: Record<RecurrenceType, { active: number; atRisk: number }> = {
  daily: { active: 2, atRisk: 7 },         // 2 dias / 7 dias
  weekly: { active: 3, atRisk: 14 },       // 3 dias / 14 dias
  monthly: { active: 7, atRisk: 30 },      // 7 dias / 30 dias
  bimonthly: { active: 10, atRisk: 30 },   // 10 dias / 30 dias
  quarterly: { active: 15, atRisk: 45 },   // 15 dias / 45 dias
  semiannual: { active: 30, atRisk: 60 },  // 30 dias / 60 dias
  annual: { active: 30, atRisk: 90 },      // 30 dias / 90 dias
  lifetime: { active: 365, atRisk: 730 },  // 1 ano / 2 anos
  custom: { active: 7, atRisk: 30 },       // Padrão mensal
};

export function calculateCustomerStatus(data: StatusCalculationData): CustomerStatus {
  // Sem transações
  if (!data.lastTransactionDate) {
    return { variant: 'outline', label: 'Sem Transações' };
  }

  const now = Date.now();
  const lastTransaction = new Date(data.lastTransactionDate).getTime();
  
  // Se temos period_end_date (data calculada de fim do período), usar ela como referência
  const referenceDate = data.periodEndDate 
    ? new Date(data.periodEndDate).getTime()
    : lastTransaction;

  const daysSinceReference = Math.floor((now - referenceDate) / (1000 * 60 * 60 * 24));

  // Se não temos tipo de recorrência, usar lógica simples (fallback)
  if (!data.recurrenceType) {
    if (daysSinceReference <= 35) return { variant: 'default', label: 'Ativo' };
    if (daysSinceReference <= 90) return { variant: 'secondary', label: 'Em Risco' };
    return { variant: 'destructive', label: 'Churned' };
  }

  // Usar tolerâncias específicas do tipo de plano
  const tolerance = TOLERANCE_CONFIG[data.recurrenceType];

  if (daysSinceReference <= tolerance.active) {
    return { variant: 'default', label: 'Ativo' };
  }
  
  if (daysSinceReference <= tolerance.atRisk) {
    return { variant: 'secondary', label: 'Em Risco' };
  }

  return { variant: 'destructive', label: 'Churned' };
}
